import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import BrandGhostie from '../components/BrandGhostie';
import PageMascots from '../components/PageMascots';
import { FALLBACK_CITIES } from '../lib/cities';

export const citySlug = (city) => city.name
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

function normalizeCity(city) {
  const name = city.name || city.city || '';
  return { ...city, name, city: city.city || name };
}

function uniqueCities(list) {
  const bySlug = new Map();
  (list || []).map(normalizeCity).forEach((city) => {
    if (!city.name) return;
    const key = citySlug(city);
    const existing = bySlug.get(key);
    bySlug.set(key, { ...city, ...(existing || {}) });
  });
  return Array.from(bySlug.values());
}

const CATEGORY_ORDER = ['Europe', 'North America', 'South America', 'Asia & Oceania', 'Africa'];

const CITY_CATEGORY = {
  London: 'Europe',
  Venice: 'Europe',
  Turin: 'Europe',
  Naples: 'Europe',
  Rome: 'Europe',
  York: 'Europe',
  Edinburgh: 'Europe',
  Prague: 'Europe',
  Barcelona: 'Europe',
  Amsterdam: 'Europe',
  Vienna: 'Europe',
  Salem: 'North America',
  Savannah: 'North America',
  'New Orleans': 'North America',
  'Mexico City': 'North America',
  'Buenos Aires': 'South America',
  Lima: 'South America',
  Cartagena: 'South America',
  Bogotá: 'South America',
  'La Paz': 'South America',
  Istanbul: 'Asia & Oceania',
  Tokyo: 'Asia & Oceania',
  Kyoto: 'Asia & Oceania',
  Bangkok: 'Asia & Oceania',
  'Hong Kong': 'Asia & Oceania',
  Cairo: 'Africa',
};

export const DEFAULT_CITY_IMAGE = 'https://images.unsplash.com/photo-1519608487953-e999c86e7455?auto=format&fit=crop&w=1200&q=80';

const CITY_IMAGE_URL = {
  London: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=900&q=80',
  Venice: 'https://images.unsplash.com/photo-1514890547357-a9ee288728e0?auto=format&fit=crop&w=900&q=80',
  Turin: 'https://images.unsplash.com/photo-1555992828-ca4dbe41d294?auto=format&fit=crop&w=900&q=80',
  Naples: 'https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=900&q=80',
  Rome: 'https://images.unsplash.com/photo-1529260830199-42c24126f198?auto=format&fit=crop&w=900&q=80',
  York: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?auto=format&fit=crop&w=900&q=80',
  Edinburgh: 'https://images.unsplash.com/photo-1506377585622-bedcbb027afc?auto=format&fit=crop&w=900&q=80',
  Salem: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
  Prague: 'https://images.unsplash.com/photo-1519677100203-a0e668c92439?auto=format&fit=crop&w=900&q=80',
  'New Orleans': 'https://images.unsplash.com/photo-1571893544028-06b07af6dade?auto=format&fit=crop&w=900&q=80',
  Istanbul: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=900&q=80',
  Tokyo: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=900&q=80',
  Kyoto: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?auto=format&fit=crop&w=900&q=80',
  Bangkok: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=900&q=80',
  'Hong Kong': 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?auto=format&fit=crop&w=900&q=80',
  Barcelona: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?auto=format&fit=crop&w=900&q=80',
  Amsterdam: 'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?auto=format&fit=crop&w=900&q=80',
  Vienna: 'https://images.unsplash.com/photo-1516550893923-42d28e5677af?auto=format&fit=crop&w=900&q=80',
  Cairo: 'https://images.unsplash.com/photo-1572252009286-268acec5ca0a?auto=format&fit=crop&w=900&q=80',
  Savannah: 'https://images.unsplash.com/photo-1551887373-6dc6ad4c2f08?auto=format&fit=crop&w=900&q=80',
  'Mexico City': 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?auto=format&fit=crop&w=900&q=80',
  'Buenos Aires': 'https://images.unsplash.com/photo-1589909202802-8f4aadce1849?auto=format&fit=crop&w=900&q=80',
  Lima: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?auto=format&fit=crop&w=900&q=80',
  Cartagena: 'https://images.unsplash.com/photo-1583531352515-8884af319dc1?auto=format&fit=crop&w=900&q=80',
  Bogotá: 'https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=900&q=80',
  'La Paz': 'https://images.unsplash.com/photo-1512813195386-6cf811ad3542?auto=format&fit=crop&w=900&q=80',
};

export function stockImage(city) {
  return CITY_IMAGE_URL[city.name] || DEFAULT_CITY_IMAGE;
}

function CityCard({ city }) {
  return (
    <Link
      to={`/cities/${citySlug(city)}`}
      className="group block overflow-hidden rounded-[1.75rem] border border-[#3f2e1e] bg-[#171717] transition-all duration-300 hover:-translate-y-1 hover:border-[#f97316]/70 hover:shadow-[0_0_30px_rgba(249,115,22,0.16)]"
      aria-label={`Open ${city.name} city page`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={stockImage(city)}
          alt={`${city.name} dark lore atmosphere`}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = DEFAULT_CITY_IMAGE;
          }}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <h3 className="font-heading text-3xl sm:text-4xl font-semibold text-white drop-shadow-lg">
            {city.name}
          </h3>
        </div>
      </div>
    </Link>
  );
}

export default function Cities() {
  const [cities, setCities] = useState(uniqueCities(FALLBACK_CITIES));
  useEffect(() => {
    api.get('/cities')
      .then(r=>setCities(uniqueCities(r.data?.length ? r.data : FALLBACK_CITIES)))
      .catch(()=>{});
  }, []);

  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(CATEGORY_ORDER.map(category => [category, []]));
    [...cities]
      .sort((a,b)=>a.name.localeCompare(b.name))
      .forEach(city => {
        const category = CITY_CATEGORY[city.name] || 'Asia & Oceania';
        buckets[category].push(city);
      });
    return buckets;
  }, [cities]);

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 overflow-visible">
      <PageMascots />
      <div className="relative z-10">
      <div className="badge-cozy mb-4">Cities</div>
      <h1 className="text-4xl sm:text-6xl font-heading font-semibold mb-5">
        Where <BrandGhostie /> already wanders.
      </h1>
      <p className="text-lg text-[#a3a3a3] max-w-3xl leading-relaxed mb-12">
        Choose a city and step into its strange layer: folklore, haunted corners, witchy cafés, old cemeteries, secret alleys and stories that locals keep alive.
      </p>

      <div className="space-y-14">
        {CATEGORY_ORDER.map(category => (
          <section key={category}>
            <div className="flex items-center gap-4 mb-5">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold">{category}</h2>
              <div className="h-px flex-1 bg-[#3f2e1e]" />
            </div>

            {grouped[category].length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {grouped[category].map(city => <CityCard key={city.city_id || citySlug(city)} city={city} />)}
              </div>
            ) : (
              <div className="magical-card p-6 text-sm text-[#a3a3a3]">
                Coming soon. The map is still whispering.
              </div>
            )}
          </section>
        ))}
      </div>
      </div>
    </div>
  );
}
